import { Fragment } from 'react';
import { css } from '@emotion/react';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Button, Colors, Heading, HeadingLevel, Icon, Link, Shadows, Typography, } from '../../../index';
const breakpoints = {
    tablet: 768,
    desktop: 1230,
};
const ContentStyles = {
    iconContainer: css `
    margin-bottom: 15px;
  `,
    mediaContainer: css `
    width: 100%;
    margin-bottom: 20px;
  `,
    heading: [
        css `
      margin-bottom: 10px;
      color: ${Colors.neutral800};
      ${Typography.pageHeader9}
    `,
    ],
    description: [
        css `
      margin-bottom: 10px;
      color: ${Colors.neutral800};
      ${Typography.paragraph3}

      @media screen and (min-width: ${breakpoints.tablet}px) {
        ${Typography.paragraph1Short}
      }
    `,
    ],
    smallText: [
        css `
      margin-bottom: 10px;
      color: ${Colors.neutral700};
      ${Typography.smallPrint1}

      @media screen and (min-width: ${breakpoints.tablet}px) {
        ${Typography.paragraph3}
      }
    `,
    ],
};
const PrimaryCTAStyles = {
    primaryCtaContainer: css `
    & > a,
    & > button {
      width: 100%;
    }

    @media screen and (min-width: ${breakpoints.tablet}px) {
      & > a,
      & > button {
        width: auto;
      }
    }
  `,
};
const SecondaryCTAStyles = {
    secondaryCtaContainer: css `
    margin-top: 10px;

    & > a {
      display: inline-flex;
      align-items: center;
    }
  `,
    secondaryCtaText: css `
    ${Typography.paragraph3Bold}
  `,
    secondaryCtaIconContainer: css `
    margin-left: 6px;
    height: 24px;
  `,
};
const Styles = labelStyles('Block', Object.assign(Object.assign(Object.assign({ container: (isCentered) => [
        css `
      background-color: transparent;
      display: flex;
      flex-direction: column;
      margin: 0 auto;
      align-items: ${isCentered ? 'center' : 'flex-start'};
      text-align: ${isCentered ? 'center' : 'left'};
    `,
    ], link: css `
    &:hover img {
      box-shadow: ${Shadows.elevation20};
    }
  ` }, ContentStyles), PrimaryCTAStyles), SecondaryCTAStyles));
const Content = ({ iconName, iconColor, media, heading, description, smallText, }) => {
    return (isolateComponent(Fragment, null,
        iconName && (isolateComponent("div", { "data-testid": "icon", css: Styles.iconContainer },
            isolateComponent(Icon, { color: iconColor, name: iconName, size: 50 }))),
        media && isolateComponent("div", { css: Styles.mediaContainer }, media),
        heading && (isolateComponent(HeadingLevel, null,
            isolateComponent(Heading, { css: Styles.heading }, heading))),
        description && isolateComponent("div", { css: Styles.description }, description),
        smallText && isolateComponent("p", { css: Styles.smallText }, smallText)));
};
const PrimaryCTA = ({ ctaHref, onCtaClick, onCtaKeyDown, ctaText, }) => (isolateComponent("div", { css: Styles.primaryCtaContainer },
    isolateComponent(Button, { color: "primary", href: ctaHref, onClick: onCtaClick, onKeyDown: onCtaKeyDown, "data-testid": "cta-button" }, ctaText)));
const SecondaryCTA = ({ secondaryCtaHref, onSecondaryCtaClick, onSecondaryCtaKeyDown, secondaryCtaText, }) => {
    return (isolateComponent("div", { css: Styles.secondaryCtaContainer },
        isolateComponent(Link, { "data-testid": "cta-secondary-button", href: secondaryCtaHref, onClick: onSecondaryCtaClick, onKeyDown: onSecondaryCtaKeyDown, weight: "bold" },
            isolateComponent("div", { css: Styles.secondaryCtaText }, secondaryCtaText))));
};
export function Block({ isLink = false, isCentered = false, description, iconName, iconColor = 'blue', media, ctaText, ctaHref, secondaryCtaText, secondaryCtaHref, heading, smallText, onCtaClick, onCtaKeyDown, onSecondaryCtaClick, onSecondaryCtaKeyDown, }) {
    const hasPrimaryCta = Boolean(ctaText && (ctaHref || onCtaClick));
    const hasSecondaryCta = Boolean(secondaryCtaText && (secondaryCtaHref || onSecondaryCtaClick));
    const contentProps = {
        iconName,
        iconColor,
        media,
        heading,
        description,
        smallText,
    };
    const primaryCTAProps = {
        ctaHref,
        onCtaClick,
        onCtaKeyDown,
        ctaText,
    };
    const secondaryCTAProps = {
        secondaryCtaHref,
        onSecondaryCtaClick,
        onSecondaryCtaKeyDown,
        secondaryCtaText,
    };
    return (isolateComponent("div", { css: Styles.container(isCentered) }, isLink ? (isolateComponent(Link, { href: ctaHref, "data-testid": "link-wrapper", onClick: onCtaClick, onKeyDown: onCtaKeyDown, css: Styles.link },
        isolateComponent(Content, Object.assign({}, contentProps)))) : (isolateComponent(Fragment, null,
        isolateComponent(Content, Object.assign({}, contentProps)),
        hasPrimaryCta && isolateComponent(PrimaryCTA, Object.assign({}, primaryCTAProps)),
        hasSecondaryCta && isolateComponent(SecondaryCTA, Object.assign({}, secondaryCTAProps))))));
}
//# sourceMappingURL=Block.js.map
// @zapier/design-system@6.28.0 (14d5b99c8)
